import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Badge } from 'react-bootstrap';
import { 
  Users, Eye, CheckCircle, MessageCircle, Wallet, 
  Star, TrendingUp, Phone, Mail, Clock, Briefcase, 
  FileText, Activity, Lock, ChevronRight, MapPin
} from 'lucide-react';
import Navbar from '../../Pages/Navbar/JobSeekerNavbar';
import Footer from "../../Footer/Footer";
import ProfileCard from '../ProfileCard/ProfileCard';
import BadgePromotionSection from './BadgePromotionSection';
import BaseUrl from '../../ApiServices/BaseUrl';
import Swal from 'sweetalert2';
import "./Dashboard.css";

const Dashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState({});
    const [profilesVisited, setProfilesVisited] = useState({ today: 0, total: 0 });
    const [messageCount, setMessageCount] = useState(0);
    const [recentMessages, setRecentMessages] = useState([]);
    const [applicationsData, setApplicationsData] = useState([]);
    const [employerJobs, setEmployerJobs] = useState([]);
    const [currentSub, setCurrentSub] = useState(null);
    const [fullProfile, setFullProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem('user')) || {};
        const userId = userData.id || localStorage.getItem('userId');
        setUser(userData);
        
        if (userId) {
            fetchAllData(userId);
            fetchFullProfile(userId);
        }
    }, []);

    const fetchFullProfile = async (userId) => {
        try {
            const response = await fetch(`${BaseUrl}/api/job-seeker/${userId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.profile_photo) {
                    data.profile_photo = `${BaseUrl}${data.profile_photo}`;
                }
                setFullProfile(data);
            }
        } catch (err) {
            console.error('Error fetching full profile:', err);
        }
    };

    const fetchAllData = async (userId) => {
        setLoading(true);
        try {
            const response = await fetch(`${BaseUrl}/api/job-seeker/dashboard-summary/${userId}`);
            if (!response.ok) throw new Error("Failed to fetch dashboard summary");
            
            const data = await response.json();
            
            if (data.success) {
                // 1. Set Profile Visited stats
                setProfilesVisited({ 
                    today: data.stats?.profiles_visited?.today || 0, 
                    total: data.stats?.profiles_visited?.total || 0 
                });

                // 2. Set Message Count & Recent Messages
                const allMessages = data.recent_messages || [];
                const conversationsMap = {};
                
                allMessages.forEach(msg => {
                    const key = `${msg.job_id}_${msg.emp_id}`;
                    if (!conversationsMap[key]) {
                        conversationsMap[key] = {
                            key,
                            job_id: msg.job_id,
                            emp_id: msg.emp_id,
                            emp_name: msg.agency_name || ((msg.emp_first_name || '') + ' ' + (msg.emp_last_name || '')).trim() || 'Employer',
                            job_profile_photo: (!msg.job_id && msg.agency_photo) 
                                ? (msg.agency_photo.startsWith('/') ? msg.agency_photo : '/' + msg.agency_photo.replace(/\\/g, '/'))
                                : msg.job_profile_photo,
                            messages: [],
                            lastMessageTime: msg.created_at,
                        };
                    }
                    conversationsMap[key].messages.push(msg);
                    if (new Date(msg.created_at) > new Date(conversationsMap[key].lastMessageTime)) {
                        conversationsMap[key].lastMessageTime = msg.created_at;
                    }
                });

                const sortedConversations = Object.values(conversationsMap).sort(
                    (a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
                );
                
                setMessageCount(data.stats?.total_messages || 0);
                setRecentMessages(sortedConversations.slice(0, 6));

                // 3. Set Applications
                setApplicationsData(data.applications || []);
                
                // 4. Set Subscription
                setCurrentSub(data.subscription || null);

                // Fetch all jobs for recommendations
                const jobsRes = await fetch(`${BaseUrl}/api/employer/all`);
                if (jobsRes.ok) {
                    const jobsData = await jobsRes.json();
                    setEmployerJobs(jobsData);
                }
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getJobTitle = (jobid) => {
        const job = employerJobs.find(j => j.id === jobid);
        return job ? (job.job_title || job.emp_name) : `Job #${jobid}`;
    };

    const getCompanyName = (jobid) => {
        const job = employerJobs.find(j => j.id === jobid);
        return job ? (job.emp_name || 'Employer') : 'Employer';
    };

    const handleCancelApplication = async (appId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Do you want to cancel this application?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3d6e6e',
            confirmButtonText: 'Yes, cancel it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${BaseUrl}/api/applications/${appId}`, {
                    method: 'DELETE'
                });

                if (response.ok) {
                    Swal.fire('Cancelled!', 'Your application has been cancelled.', 'success');
                    fetchAllData(user.id);
                } else {
                    Swal.fire('Error', 'Failed to cancel application.', 'error');
                }
            } catch (error) {
                console.error('Error cancelling application:', error);
                Swal.fire('Error', 'An unexpected error occurred.', 'error');
            }
        }
    };

    const getEmployerContact = (jobid) => {
        const job = employerJobs.find(j => j.id === jobid);
        return {
            email: job?.email_id || 'N/A',
            phone: job?.whatsapp_number || job?.phone_number || 'N/A'
        };
    };

    return (
        <div className="redesigned-dashboard">
            <Navbar />
            
            <Container fluid className="dashboard-container">
                <Row className="stats-row g-2">
                    <Col xs={6} md={3}>
                        <div className="stat-card gold">
                            <div className="stat-title">Profile Strength</div>
                            <div className="stat-value">85%</div>
                            <div className="stat-footer">
                                <TrendingUp size={14} /> Actively Looking
                            </div>
                        </div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="stat-card blue">
                            <div className="stat-title">Applications</div>
                            <div className="stat-value">{applicationsData.length}</div>
                            <div className="stat-footer">
                                <ChevronRight size={14} /> Track Applications
                            </div>
                        </div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="stat-card green">
                            <div className="stat-title">Profile Views</div>
                            <div className="stat-value">{profilesVisited.total}</div>
                            <div className="stat-footer">
                                <Eye size={14} /> Total Views
                            </div>
                        </div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="stat-card purple">
                            <div className="stat-title">Job Matches</div>
                            <div className="stat-value">32</div>
                            <div className="stat-footer">
                                <Star size={14} /> Recommended for you
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row className="g-4">
                    {/* Left Column */}
                    <Col lg={8}>
                        {/* Badge Promotion Section */}
                        <BadgePromotionSection 
                            profile={fullProfile} 
                            onPurchaseSuccess={() => {
                                const userId = user.id || localStorage.getItem('userId');
                                fetchFullProfile(userId);
                                fetchAllData(userId);
                            }} 
                        />

                        {/* Applied Jobs Section */}
                        <div className="dashboard-section p-4">
                            <div className="section-header mb-4">
                                <h4 className="section-title m-0"><Briefcase size={20} /> Applied Jobs</h4>
                                <a href="/appliedjobs" className="view-all-btn">View All</a>
                            </div>
                            <div className="scrollable-table-container">
                                <div className="table-responsive">
                                    <table className="applied-jobs-table">
                                        <thead>
                                            <tr>
                                                <th>Position</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Date</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {applicationsData.length > 0 ? applicationsData.map((app, idx) => {
                                                const contact = getEmployerContact(app.jobid);
                                                return (
                                                    <tr key={idx}>
                                                        <td 
                                                            className="cursor-pointer"
                                                            onClick={() => navigate(`/jobdetails/${app.jobid}`)}
                                                            title="View Job Details"
                                                        >
                                                            <div className="fw-bold text-dark">{getJobTitle(app.jobid)}</div>
                                                            <div className="small text-muted">{getCompanyName(app.jobid)}</div>
                                                        </td>
                                                        <td>
                                                            <a href={`mailto:${contact.email}`} className="email-link-btn" title={contact.email}>
                                                                <Mail size={18} />
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <a href={`tel:${contact.phone}`} className="phone-link-btn" title={contact.phone}>
                                                                <Phone size={18} />
                                                            </a>
                                                        </td>
                                                        <td>{new Date(app.application_date).toLocaleDateString('en-GB')}</td>
                                                        <td>
                                                            <button 
                                                                className="cancel-app-btn"
                                                                onClick={() => handleCancelApplication(app.id)}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center text-muted py-4">No applications yet.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Recent Messages Section */}
                        <div className="dashboard-section">
                            <div className="section-header">
                                <h4 className="section-title"><MessageCircle size={20} /> Recent Messages</h4>
                                <a href="/jobseeker-messages" className="view-all-btn">View All</a>
                            </div>
                            <div className="message-list">
                                {recentMessages.length > 0 ? recentMessages.map((conv, idx) => {
                                    const lastMsg = conv.messages[conv.messages.length - 1];
                                    const msgDate = new Date(conv.lastMessageTime);
                                    const now = new Date();
                                    const diffMs = now - msgDate;
                                    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
                                    const diffDays = Math.floor(diffHrs / 24);
                                    let timeStr = '';
                                    if (diffHrs < 1) {
                                        const diffMins = Math.floor(diffMs / (1000 * 60));
                                        timeStr = diffMins <= 0 ? 'Just now' : `${diffMins}m ago`;
                                    } else if (diffHrs < 24) {
                                        timeStr = `${diffHrs}h ago`;
                                    } else if (diffDays === 1) {
                                        timeStr = 'Yesterday';
                                    } else {
                                        timeStr = msgDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                                    }
                                    return (
                                        <div 
                                            key={idx} 
                                            className="message-item"
                                            onClick={() => navigate('/jobseeker-messages', { state: { selectedConversation: conv } })}
                                        >
                                            {conv.job_profile_photo ? (
                                                <img src={`${BaseUrl}${conv.job_profile_photo}`} alt="Avatar" className="message-avatar" />
                                            ) : (
                                                <div className="message-avatar initial-avatar">
                                                    {conv.emp_name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="message-content">
                                                <div className="d-flex justify-content-between">
                                                    <div className="message-sender">{conv.emp_name}</div>
                                                    <div className="message-time">{timeStr}</div>
                                                </div>
                                                <div className="message-text">
                                                    {lastMsg?.sender_id === user.id ? 'You: ' : ''}
                                                    {lastMsg?.message || 'No message content'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <p className="text-center text-muted py-4">No recent messages.</p>
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* Right Column */}
                    <Col lg={4}>
                        {/* Profile Summary Section */}
                        <div className="dashboard-section p-0 overflow-hidden">
                            <div className="px-4 pt-4">
                                <h4 className="section-title"><Users size={20} /> My Profile Summary</h4>
                            </div>
                            <div className="p-2">
                                <ProfileCard />
                            </div>
                        </div>

                        {/* Wallet / Plan Section */}
                        <div className="dashboard-section wallet-card">
                            <div className="section-header">
                                <h4 className="section-title"><Wallet size={20} /> Wallet/Plan</h4>
                            </div>
                            <div className="wallet-stats">
                                <div className="wallet-stat-item">
                                    <div className="wallet-stat-icon"><Briefcase size={20} /></div>
                                    <div className="wallet-stat-value">240</div>
                                    <div className="wallet-stat-label">Currency (AED)</div>
                                </div>
                                <div className="wallet-stat-item">
                                    <div className="wallet-stat-icon"><FileText size={20} /></div>
                                    <div className="wallet-stat-value">6</div>
                                    <div className="wallet-stat-label">CV Unlocks Used</div>
                                </div>
                                <div className="wallet-stat-item">
                                    <div className="wallet-stat-icon"><MessageCircle size={20} /></div>
                                    <div className="wallet-stat-value">3</div>
                                    <div className="wallet-stat-label">Chats Used</div>
                                </div>
                                <div className="wallet-stat-item">
                                    <div className="wallet-stat-icon"><Lock size={20} /></div>
                                    <div className="wallet-stat-value">{currentSub?.plan_name || 'Free'}</div>
                                    <div className="wallet-stat-label">Plan Status</div>
                                </div>
                            </div>
                             <button className="recharge-btn" onClick={() => navigate('/candidate-subscription')}>
                                 Recharge Wallet
                             </button>
                         </div>
 
                         {/* Recommended Jobs Section */}
                         <div className="dashboard-section">
                             <div className="section-header">
                                 <h4 className="section-title"><Star size={20} /> Recommended</h4>
                                 <a href="/viewjobs" className="view-all-btn">See More</a>
                             </div>
                             <div className="recommended-list">
                                 {employerJobs
                                     .filter(job => !applicationsData.some(app => app.jobid === job.id))
                                     .slice(0, 3)
                                     .map((job, idx) => (
                                     <div 
                                         key={job.id} 
                                         className="rec-job-item cursor-pointer"
                                         onClick={() => navigate(`/jobdetails/${job.id}`)}
                                     >
                                         <div className="rec-job-title">{job.job_title || 'General Worker'}</div>
                                         <div className="rec-job-meta">
                                             <span className="d-flex align-items-center gap-1"><MapPin size={14} /> {job.work_location || 'Doha, Qatar'}</span>
                                         </div>
                                         <div className="rec-job-footer">
                                             <span className="rec-job-type-text">
                                                 {job.domestic_worker_category || 'Domestic Helper'} | {job.job_type || 'Full Time'}
                                             </span>
                                             <button className="apply-btn-sm">View</button>
                                         </div>
                                     </div>
                                 ))}
                                 {employerJobs.length === 0 && (
                                     <p className="text-center text-muted py-4">No recommendations found.</p>
                                 )}
                             </div>
                         </div>
                    </Col>
                </Row>
            </Container>
            <Footer />
        </div>
    );
};

export default Dashboard;
